const DEFAULT_API_VERSION = '2026-07';
const WISHLIST_EVENT_NAME = 'wishlist:changed';
const PAGE_SIZE = 24;
const CARD_CONCURRENCY = 6;

function parseJSONScript(element, fallback = null) {
  if (!(element instanceof HTMLScriptElement)) {
    return fallback;
  }

  try {
    return JSON.parse(element.textContent || '');
  } catch (error) {
    console.error('Unable to parse wishlist JSON payload.', error);
    return fallback;
  }
}

function getProductCode(productId) {
  if (typeof productId !== 'string') {
    return '';
  }

  return productId.split('/').pop() || '';
}

if (!customElements.get('user-wishlist')) {
  customElements.define(
    'user-wishlist',
    class UserWishlist extends HTMLElement {
      connectedCallback() {
        if (this.initialized) {
          document.addEventListener(WISHLIST_EVENT_NAME, this.handleWishlistChanged);
          this.loadMore?.addEventListener('click', this.handleLoadMore);
          return;
        }

        this.initialized = true;
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.loading = this.querySelector('[data-wishlist-loading]');
        this.empty = this.querySelector('[data-wishlist-empty]');
        this.error = this.querySelector('[data-wishlist-error]');
        this.loadMore = this.querySelector('[data-wishlist-load-more]');
        this.pagination = this.querySelector('[data-wishlist-pagination]');
        this.messages = parseJSONScript(this.querySelector('[data-user-wishlist-messages]'), {}) || {};
        this.wishlists = parseJSONScript(this.querySelector('[data-user-wishlist-data]'), {}) || {};
        this.shopDomain = this.dataset.shopDomain || window.location.host;
        this.storefrontAccessToken = this.dataset.storefrontAccessToken || '';
        this.apiVersion = this.dataset.apiVersion || DEFAULT_API_VERSION;
        this.isDesignMode = this.dataset.designMode === 'true';
        this.productIds = this.getWishlistProductIds();
        this.pendingProductIds = [...this.productIds];
        this.removedProductIds = new Set();
        this.isLoading = false;
        this.handleWishlistChanged = this.handleWishlistChanged.bind(this);
        this.handleLoadMore = () => this.loadWishlist();

        document.addEventListener(WISHLIST_EVENT_NAME, this.handleWishlistChanged);
        this.loadMore?.addEventListener('click', this.handleLoadMore);
        this.loadWishlist();
      }

      disconnectedCallback() {
        document.removeEventListener(WISHLIST_EVENT_NAME, this.handleWishlistChanged);
        this.loadMore?.removeEventListener('click', this.handleLoadMore);
      }

      getWishlistProductIds() {
        const wishlist = this.wishlists?.default;

        if (!wishlist || !Array.isArray(wishlist.products)) {
          return [];
        }

        return [...new Set(wishlist.products.filter(
          (productId) => typeof productId === 'string' && /^gid:\/\/shopify\/Product\/[1-9]\d*$/.test(productId)
        ))];
      }

      async loadWishlist() {
        if (this.isLoading) {
          return;
        }

        if (this.pendingProductIds.length === 0) {
          this.setVisibleState(this.grid.children.length ? 'grid' : 'empty');
          this.updateLoadMore();
          return;
        }

        if (!this.storefrontAccessToken) {
          const message = this.isDesignMode ? this.messages.missingToken : this.messages.error;
          this.showError(message);
          this.updateLoadMore();
          return;
        }

        const restoreFocus = document.activeElement === this.loadMore;
        this.isLoading = true;
        this.updateLoadMore();
        this.showLoading();

        try {
          while (this.pendingProductIds.length > 0) {
            const batchIds = this.pendingProductIds.slice(0, PAGE_SIZE);
            const products = await this.getProducts(batchIds);
            const cards = await this.renderProductCards(
              products.filter((product) => !this.removedProductIds.has(product.id))
            );
            const visibleCards = cards.filter((card) => !this.removedProductIds.has(card.id));
            this.grid.insertAdjacentHTML('beforeend', visibleCards.map((card) => card.markup).join(''));
            const consumedIds = new Set(batchIds);
            this.pendingProductIds = this.pendingProductIds.filter((id) => !consumedIds.has(id));

            if (visibleCards.length > 0) {
              this.setVisibleState('grid');
              if (restoreFocus && this.pendingProductIds.length === 0) {
                this.grid.querySelector(
                  `[data-product-id="${getProductCode(visibleCards[0].id)}"] a[href]`
                )?.focus();
              }
              return;
            }
          }

          this.setVisibleState(this.grid.children.length ? 'grid' : 'empty');
          if (restoreFocus) {
            (this.grid.querySelector('a[href]') || this.empty)?.focus();
          }
        } catch (error) {
          console.error('Error rendering wishlist.', error);
          this.showError(this.messages.error);
        } finally {
          this.isLoading = false;
          this.updateLoadMore();
        }
      }

      async getProducts(ids) {
        const query = `
          query WishlistProducts($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on Product {
                id
                handle
              }
            }
          }
        `;
        const payload = await this.makeGraphQLRequest(query, { ids });
        const nodes = payload?.data?.nodes;

        if (!Array.isArray(nodes)) {
          throw new Error('Wishlist GraphQL response is missing product nodes.');
        }

        const productsById = new Map(
          nodes
            .filter((node) => node && typeof node.id === 'string' && typeof node.handle === 'string')
            .map((node) => [node.id, node])
        );

        return ids.map((productId) => productsById.get(productId)).filter(Boolean);
      }

      async makeGraphQLRequest(query, variables = {}) {
        const endpoint = `https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
          },
          body: JSON.stringify({
            query,
            variables,
          }),
        });

        if (!response.ok) {
          const responseBody = (await response.text()).slice(0, 1_000);
          throw new Error(
            `Wishlist GraphQL request failed (${response.status}) at ${endpoint}: ${responseBody}`
          );
        }

        const payload = await response.json();

        if (Array.isArray(payload.errors) && payload.errors.length > 0) {
          throw new Error(payload.errors.map((error) => error.message).join(', '));
        }

        return payload;
      }

      async renderProductCards(products) {
        const cards = [];
        for (let offset = 0; offset < products.length; offset += CARD_CONCURRENCY) {
          const results = await Promise.allSettled(products.slice(offset, offset + CARD_CONCURRENCY).map(async (product) => {
            const productCode = getProductCode(product.id);

            const response = await fetch(
              `/products/${product.handle}?section_id=ajax-wishlist-product-card`,
              {
                credentials: 'same-origin',
              }
            );

            if (!response.ok) {
              throw new Error(`Wishlist card request failed with status ${response.status}.`);
            }

            const markup = await response.text();

            if (!markup.trim()) {
              throw new Error('Wishlist card response is empty.');
            }

            const normalizedMarkup = markup.replaceAll(
              'shopify-section-ajax-wishlist-product-card',
              `shopify-section-ajax-wishlist-product-card-${productCode}`
            );

            return {
              id: product.id,
              markup: `<div class="user-wishlist__item" data-product-id="${productCode}">${normalizedMarkup}</div>`,
            };
          }));
          const failure = results.find((result) => result.status === 'rejected');
          if (failure) {
            throw failure.reason;
          }
          cards.push(...results.map((result) => result.value));
        }

        return cards;
      }

      handleWishlistChanged(event) {
        const productId = getProductCode(String(event.detail?.productId || ''));
        const action = event.detail?.action;

        if (!/^[1-9]\d*$/.test(productId) || action !== 'remove') {
          return;
        }

        const card = this.querySelector(`[data-product-id="${productId}"]`);

        card?.remove();
        const gid = `gid://shopify/Product/${productId}`;
        this.removedProductIds.add(gid);
        this.pendingProductIds = this.pendingProductIds.filter((id) => id !== gid);
        this.productIds = this.productIds.filter((id) => getProductCode(id) !== productId);
        this.updateLoadMore();

        if (this.grid.children.length === 0 && !this.isLoading) {
          this.loadWishlist();
        }
      }

      updateLoadMore() {
        if (this.loadMore) {
          this.loadMore.hidden = this.pendingProductIds.length === 0 || !this.storefrontAccessToken
            || (this.isLoading && this.grid.children.length === 0);
          this.loadMore.disabled = this.isLoading;
          this.loadMore.setAttribute('aria-busy', String(this.isLoading));
          if (this.pagination) {
            this.pagination.hidden = this.loadMore.hidden;
          }
        }
      }

      showLoading() {
        if (this.loading) {
          this.loading.textContent = this.messages.loading || '';
        }

        this.setVisibleState('loading');
      }

      showError(message) {
        if (this.error) {
          this.error.textContent = message || this.messages.error || '';
        }

        this.setVisibleState('error');
      }

      setVisibleState(state) {
        if (this.loading) {
          this.loading.hidden = state !== 'loading';
        }

        if (this.empty) {
          this.empty.hidden = state !== 'empty';
        }

        if (this.error) {
          this.error.hidden = state !== 'error';
        }

        if (this.grid) {
          this.grid.hidden = this.grid.children.length === 0;
          this.grid.setAttribute('aria-busy', String(state === 'loading'));
        }
      }
    }
  );
}
